import * as React from 'react';
import { Routes, Route, useParams } from 'react-router-dom';
import DraftManualProvider from './context';
import DraftManualsList from './List';
import DraftManualsDetail from './Detail';
import ManualStructure from './Detail/ManualStructure';
import { ManualStructureProvider } from './Detail/ManualStructure/context';
import DraftManualGIQ from './GIQ';
import DraftManualCRQ from './CRQ';
import AcknowledgementSettings from './AcknowledgementSettings';
import PublishApproved from './PublishApproved';
import ReviewUpdates from './Detail/ReviewUpdates';
import { SharedTrackChangesContext } from 'components/ContentEditor/context/SharedTrackChangesContext';
import { ReviewUpdatesProvider } from './Detail/ReviewUpdates/context';
import FeaturesManager from 'utils/features/FeaturesManager';
import { ManualPropertiesProvider } from './ManualProperties/context';
import { ManualProperties } from './ManualProperties';
import { PolicyEditorProvider } from './Detail/PolicyEditor/context';
import { PrefaceEditorProvider } from './Detail/PrefaceEditor/context';
import DraftManualDetailProvider from './Detail/context';
import CreateGlobalManual from './CreateGlobalManual';
import CreateJurisdictionManual from './CreateJurisdictionManual';
import CreateAgencyManual from './CreateAgencyManual';
import GenerateChangeset from './Detail/GenerateChangeset';

FeaturesManager.registerFeature({
  id: 'policy-headers',
  name: 'Policy Headers',
  description: 'Enable policy headers POC',
  enabled: false,
});

FeaturesManager.registerFeature({
  id: 'export-draft-manuals',
  name: 'Export Draft Manuals',
  description: 'Enable export functionality for draft manuals',
  enabled: false,
  managed: false,
});

// Wrapper component to extract content ID from route params and provide necessary context
const ReviewUpdatesWithContext: React.FC = () => {
  const { content } = useParams<{ content: string }>();

  if (content?.startsWith('policy:')) {
    const policyId = content.replace('policy:', '');
    return (
      <DraftManualDetailProvider>
        <PolicyEditorProvider id={policyId}>
          <ReviewUpdatesProvider>
            <ReviewUpdates />
          </ReviewUpdatesProvider>
        </PolicyEditorProvider>
      </DraftManualDetailProvider>
    );
  }

  if (content?.startsWith('preface:')) {
    const prefaceId = content.replace('preface:', '');
    return (
      <DraftManualDetailProvider>
        <PrefaceEditorProvider id={prefaceId}>
          <ReviewUpdatesProvider>
            <ReviewUpdates />
          </ReviewUpdatesProvider>
        </PrefaceEditorProvider>
      </DraftManualDetailProvider>
    );
  }

  // Fallback for invalid content parameter
  return <ReviewUpdates />;
};

const DraftManuals: React.FC = () => {
  return (
    <DraftManualProvider>
      <SharedTrackChangesContext>
        {/* // TODO: Remove this context from here and look for another solution. */}
        <Routes>
          <Route
            path=''
            element={<DraftManualsList />}
          />
          <Route
            path='create-global-manual'
            element={<CreateGlobalManual />}
          />
          <Route
            path='create-jurisdiction-manual/:id'
            element={<CreateJurisdictionManual />}
          />
          <Route
            path='create-agency-manual'
            element={<CreateAgencyManual />}
          />
          <Route
            path=':id'
            element={<DraftManualsDetail />}
          >
            <Route
              path=':node'
              element={<DraftManualsDetail />}
            />
          </Route>
          <Route
            path=':id/giq'
            element={<DraftManualGIQ />}
          />
          <Route
            path=':id/crq'
            element={<DraftManualCRQ />}
          />
          <Route
            path=':id/manual-properties'
            element={
              <ManualPropertiesProvider>
                <ManualProperties />
              </ManualPropertiesProvider>
            }
          />
          <Route
            path=':id/manual-structure'
            element={
              <ManualStructureProvider>
                <ManualStructure />
              </ManualStructureProvider>
            }
          />
          <Route
            path=':id/:content/review-updates'
            element={<ReviewUpdatesWithContext />}
          />
          <Route
            path=':id/acknowledgement-settings'
            element={<AcknowledgementSettings />}
          />
          <Route
            path=':id/publish-approved'
            element={<PublishApproved />}
          />
          <Route
            path=':id/:content/generate-changeset'
            element={
              <DraftManualDetailProvider>
                <GenerateChangeset />
              </DraftManualDetailProvider>
            }
          />
        </Routes>
      </SharedTrackChangesContext>
    </DraftManualProvider>
  );
};

export default DraftManuals;
